import { Component, effect, EventEmitter, OnDestroy, Output } from '@angular/core';
import { Offer } from 'ng2-app/services/offers/offer.model';
import { Approval } from 'ng2-app/models/dto/approval/approval.model';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, Subject, take, firstValueFrom, of } from 'rxjs';
import { transition, trigger, useAnimation } from '@angular/animations';
import { fadeIn, fadeOut } from 'ng-animate';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { AttachDeclineReasonsToApproval } from 'ng2-app/store/offers/approvals.actions';
import { AttachDeclineReasonsToOffer } from 'ng2-app/store/offers/offer.actions';
import { AttachDeclineReasonsToOption } from 'ng2-app/store/offers/offer-options.actions';
import { catchError, map } from 'rxjs/operators';
import { DeclineReasonState } from 'ng2-app/store/decline-reasons/decline-reasons.state';
import { DeclineReason } from 'ng2-app/models/dto/decline-reason.model';
import { MatDialogRef } from '@angular/material/dialog';
import { GetDeclineReasons } from 'app/store/decline-reasons/decline-reasons.actions';

type FinancingOption = Offer | OfferOption | Approval;
type PartialDeclineReason = Pick<DeclineReason, 'id' | 'comment'>;

@Component({
  selector: 'app-decline-reasons-dialog-smart',
  templateUrl: './decline-reasons-dialog-smart.component.html',
  styleUrls: ['./decline-reasons-dialog-smart.component.scss'],
  animations: [
    trigger('listAnimation', [
      transition(':enter', useAnimation(fadeIn, {params: {timing: 0.3}})),
      transition(':leave', useAnimation(fadeOut, {params: {timing: 0.3}})),
    ]),
  ],
  standalone: false
})
export class DeclineReasonsDialogSmartComponent implements OnDestroy {
  @Output() optionDeclined = new EventEmitter<object>();

  otherReasonValue = '';
  declineReasons$: Observable<DeclineReason[]> = this.store.select(DeclineReasonState.declineReasons);
  isLoading$: Observable<boolean> = this.store.select(DeclineReasonState.loading);

  activeReasons$: Observable<DeclineReason[]> = this.declineReasons$.pipe(
    map((declineReasons) => declineReasons.filter(item => item.reason.toLowerCase() !== 'other'))
  );
  otherReason$: Observable<DeclineReason | undefined> = this.declineReasons$.pipe(
    map((declineReasons) => declineReasons.find(item => item.reason.toLowerCase() === 'other'))
  );
  selectedReasons$ = new BehaviorSubject<PartialDeclineReason[]>([]);

  saving: boolean = false;
  destroyed$ = new Subject<boolean>();

  option: FinancingOption;
  optionType: string;

  constructor(
    private dialogRef: MatDialogRef<DeclineReasonsDialogSmartComponent>,
    private store: Store,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    effect(() => {
      this.store.dispatch(new GetDeclineReasons());
    });
    if (data?.option) {
      this.option = data.option;
    }
    if (data?.optionType) {
      this.optionType = data.optionType;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  async onSave() {
    const actionMap = {
      'approval': AttachDeclineReasonsToApproval,
      'offer': AttachDeclineReasonsToOffer,
      'option': AttachDeclineReasonsToOption,
    };
    const action = actionMap[this.optionType];
    if (!action) {
      throw new Error(`Decline reasons are not supported for ${this.optionType}`);
    }

    const declineReasons = this.selectedReasons$.getValue();
    const otherReason = await firstValueFrom(this.otherReason$);

    if (otherReason && this.otherReasonValue.trim()) {
      declineReasons.push({
        id: otherReason.id,
        comment: this.otherReasonValue,
      });
    }

    this.saving = true;
    this.store.dispatch(new action(this.option.id, declineReasons)).pipe(take(1), catchError(() => {
      this.saving = false;
      return of();
    })).subscribe(() => {
      this.saving = false;
      this.dialogRef.close();
    });
  }

  onReasonSelected(reason: DeclineReason, checked: boolean) {
    const currentReasons = this.selectedReasons$.getValue();
    if (checked) {
      this.selectedReasons$.next([...currentReasons, { id: reason.id }]);
    } else {
      this.selectedReasons$.next(currentReasons.filter((item) => item.id !== reason.id));
    }
  }

  closeModal() {
    this.dialogRef.close();
  }
}
